// Free shipping constants and helpers
export const FREE_SHIPPING_SERVICE_CODE = 'FREE_SHIPPING'

/**
 * Creates a free shipping rate object for groups with no shipping charges
 * @param {Array<{cart_item_id: number, quantity: number}>} lineItems - Line items for this group
 * @param {number} [customerAddressId] - Customer address ID for this group
 * @returns {Object} Free shipping rate object
 */
export function createFreeShippingRate(lineItems, customerAddressId) {
  return {
    service_code: FREE_SHIPPING_SERVICE_CODE,
    cost_in_cents: 0,
    encrypted_cost: null,
    carrier_service_id: null,
    cart_item_ids: lineItems.map(li => li.cart_item_id),
    customer_address_id: customerAddressId,
    is_free_shipping: true
  }
}

/**
 * Checks if a rate represents free shipping
 * @param {Object} rate - The rate object to check
 * @returns {boolean}
 */
export function isFreeShippingRate(rate) {
  return rate?.is_free_shipping === true
}

export function parseCarrierServiceGroupIds(groupId) {
  if (!groupId) return []
  return groupId.split(',').map(id => parseInt(id.trim(), 10))
}

export function organizeRatesByCarrier(rates) {
  if (!rates?.length) return {}

  const organized = {}

  rates.forEach(rate => {
    const carrier = rate.carrier_name
    if (!organized[carrier]) {
      organized[carrier] = []
    }
    organized[carrier].push(rate)
  })

  return organized
}

export function calculateTotalShippingCost(selectedRatesByGroup) {
  if (!selectedRatesByGroup || Object.keys(selectedRatesByGroup).length === 0) {
    return 0
  }

  return Object.values(selectedRatesByGroup).reduce((total, rate) => {
    return total + (rate?.cost_in_cents || 0)
  }, 0)
}

/**
 * Build cart items for a shipping group using line_items from the server response.
 * Merges line_item data (quantity, address) with full cart item details (name, image, price).
 *
 * @param {Array} lineItems - Line items from the shipping group (cart_item_id, quantity, customer_address_id)
 * @param {Array} cartItems - Full cart items from Redux state
 * @returns {Array} Cart items with group-specific quantity
 */
export function buildGroupCartItems(lineItems, cartItems) {
  if (!lineItems?.length || !cartItems?.length) return []

  return lineItems
    .map(lineItem => {
      const cartItemId = parseInt(lineItem.cart_item_id, 10)
      const fullCartItem = cartItems.find(item => parseInt(item.cart_item_id, 10) === cartItemId)

      if (!fullCartItem) return null

      // Return cart item with group-specific quantity
      return {
        ...fullCartItem,
        quantity: lineItem.quantity
      }
    })
    .filter(Boolean)
}

export function findCheapestRateInGroup(rates) {
  if (!rates?.length) return null

  return rates.reduce((cheapest, rate) => {
    if (!cheapest || rate.cost_in_cents < cheapest.cost_in_cents) {
      return rate
    }
    return cheapest
  }, null)
}

/**
 * Convert selectedRatesByGroup object to array format for backend
 * @param {Object.<string, {service_code: string, cost_in_cents: number, encrypted_cost: string, carrier_service_id: number, cart_item_ids: number[], customer_address_id: number}>} selectedRatesByGroup
 * @returns {Array<{group_id: string, service_code: string, cost_in_cents: number, encrypted_cost: string, carrier_service_id: number, cart_item_ids: number[], customer_address_id: number}>}
 */
export function buildSelectedRatesArray(selectedRatesByGroup) {
  if (!selectedRatesByGroup) return []

  return Object.entries(selectedRatesByGroup).map(([groupId, rate]) => ({
    group_id: groupId,
    service_code: rate.service_code,
    cost_in_cents: rate.cost_in_cents,
    encrypted_cost: rate.encrypted_cost,
    carrier_service_id: rate.carrier_service_id,
    cart_item_ids: rate.cart_item_ids,
    customer_address_id: rate.customer_address_id,
    is_free_shipping: rate.is_free_shipping || false
  }))
}
